import { Badge, Box, Flex, Text } from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { currency, dateTime } from "@src/utils/formatters";
import { observer } from "mobx-react-lite";
import React, { type FunctionComponent, useMemo } from "react";

const getPercentage = (total: number, number: number) => {
  const percentage = (number * 100) / total;
  return Number.isFinite(percentage) ? Math.round(percentage) : 0;
};

const getColor = (type: TooltipLegendType, isTimeAndMaterials?: boolean) => {
  switch (type) {
    case "totalBudget":
      return "yellow.400";
    case "spentBudget":
      return isTimeAndMaterials ? "yellow.400" : "teal.400";
    default:
      return "transparent";
  }
};

type TooltipLegendType = "totalBudget" | "spentBudget";

interface TooltipLegendProps {
  type: TooltipLegendType;
  isTimeAndMaterials: boolean;
  title: string;
  value: string;
  percentageValue?: number;
}

const TooltipLegend: FunctionComponent<TooltipLegendProps> = ({
  type,
  isTimeAndMaterials,
  title,
  value,
  percentageValue,
}) => {
  const color = useMemo(
    () => getColor(type, isTimeAndMaterials),
    [type, isTimeAndMaterials],
  );

  return (
    <Flex direction="column" w={{ base: "full", md: "50%" }}>
      <Text color="white" fontSize="xs">
        {title}
      </Text>
      <Flex align="center" gap="2">
        <Text color={color} fontWeight="semibold">
          {value}
        </Text>
        {typeof percentageValue === "number" && (
          <Badge px="2.5" bg={color}>
            {percentageValue}%
          </Badge>
        )}
      </Flex>
    </Flex>
  );
};

const CustomTooltip = (props: any) => {
  const active = props.active as boolean;
  const payload = props.payload as any[];
  const dateLabel = props.label as string;
  const isTimeAndMaterials = props.isTimeAndMaterials as boolean;

  if (!active || !payload.length) return null;

  const totalBudget = payload.find((i) => i.dataKey === "totalBudgetData");
  const spentBudget = payload.find((i) => i.dataKey === "spentBudgetData");

  if (!totalBudget && !spentBudget) return null;

  return (
    <Box
      pos="relative"
      top="-10px"
      left="0"
      minW={{ base: "300px", md: "380px" }}
      p="5"
      color="whiteAlpha.900"
      bg="gray.700"
      borderRadius="xl"
      outline="0"
    >
      <Text as="h1" color="white" fontSize="xs" fontWeight="semibold">
        <Trans>Date: {dateTime.formatFromString(dateLabel)}</Trans>
      </Text>

      <Flex
        wrap="wrap"
        direction={{
          base: "column",
          md: "row",
        }}
        rowGap={{ base: "1", md: "2" }}
        pt="3"
      >
        {totalBudget && !isTimeAndMaterials && (
          <TooltipLegend
            type="totalBudget"
            isTimeAndMaterials={isTimeAndMaterials}
            title={t`Budget`}
            value={currency.formatByWorkspace(totalBudget.value)}
          />
        )}
        {spentBudget && (
          <React.Fragment>
            <TooltipLegend
              type="spentBudget"
              isTimeAndMaterials={isTimeAndMaterials}
              title={t`Tracked`}
              value={currency.formatByWorkspace(spentBudget.value)}
              {...(totalBudget
                ? {
                    percentageValue: getPercentage(
                      totalBudget.value / 100,
                      spentBudget.value / 100,
                    ),
                  }
                : {})}
            />
            {/* {currency.formatByWorkspace(spentBudget.value, globalStore)}
          {totalBudget &&
            ` (${getPercentage(totalBudget.value / 100, spentBudget.value / 100)}%)`} */}
          </React.Fragment>
        )}
      </Flex>

      {isTimeAndMaterials ? (
        <Text color="white" fontSize="xs" textAlign="left">
          <Trans>Spent:</Trans>&nbsp;
          {currency.formatByWorkspace(spentBudget?.value ?? 0)}
        </Text>
      ) : (
        !!totalBudget &&
        !!spentBudget && (
          <Text color="white" fontSize="xs" textAlign="left">
            <Trans>Remaining budget:</Trans>&nbsp;
            {currency.formatByWorkspace(totalBudget.value - spentBudget.value)}
          </Text>
        )
      )}
    </Box>
  );
};

export default observer(CustomTooltip);
